/**
 * 1.68 (196) — the service charge of a table bill („მომსახურება 10%"): one rule, in whole tetri, exactly as the
 * payment trigger (`private.table_bill_pay`) counts it, so the till never sends a payment the database refuses for a
 * tetri (Codex #2).
 *
 *   base    = the stored totals (`numeric(10,2)`, half away from zero — `toTetri`) of every root line of the sale but
 *             the service's own; ingredient lines never count
 *   service = ⌊(base × bp + 5000) / 10000⌋ tetri, bp = the rate the bill opened with in basis points (10 % = 1000)
 *
 * The service is a line of the shop's own service product (`products.system_kind = 'table_service'`), quantity 1, no
 * promotion; ₾0.00 → no line. The rate is the owner's setting `table.service_pct`, read here exactly as the database
 * reads it (`private.table_service_bp`): 0–30 with at most one decimal, anything else 0.
 */
import { toTetri } from './money-round'

const PCT = /^[0-9]{1,2}(\.[0-9])?$/

/** The owner's `table.service_pct` as a number — 0 for anything the database would not take. */
export function parseServicePct(v: unknown): number {
  if (typeof v !== 'string' && typeof v !== 'number') return 0
  const s = String(v).trim()
  if (!PCT.test(s)) return 0
  const n = Number(s)
  return n <= 30 ? n : 0
}

/** 10 → 1000, 12.5 → 1250. */
export const pctToBp = (pct: number): number => Math.round(pct * 100)

/** The line's name on every paper: „მომსახურება 10%", „მომსახურება 12.5%" (documents stay Georgian). */
export function serviceName(bp: number): string {
  const pct = (bp / 100).toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
  return `მომსახურება ${pct}%`
}

export type ServiceBaseLine = { product_id: string; total_price: number; ingredient_of?: string | null }

/** The base in whole tetri: every root line as the database will store it, the service's own line left out. */
export function serviceBaseTetri(lines: readonly ServiceBaseLine[], serviceProductId: string | null): number {
  let tetri = 0
  for (const l of lines) {
    if (l.ingredient_of) continue
    if (serviceProductId && l.product_id === serviceProductId) continue
    tetri += toTetri(l.total_price)
  }
  return tetri
}

/** The service in whole tetri for a base and a rate — half up, as the trigger's integer arithmetic. */
export function serviceTetri(baseTetri: number, bp: number): number {
  if (bp <= 0 || baseTetri <= 0) return 0
  return Math.floor((baseTetri * bp + 5000) / 10000)
}

export type ServiceLine = {
  product_id: string
  product_name: string
  barcode: null
  quantity: 1
  unit_price: number
  total_price: number
  promotion_id: null
  promo_discount: 0
}

/** The line the checkout sends with the dishes; null when there is no service (0 %, ₾0.00, or no service product). */
export function serviceLine(lines: readonly ServiceBaseLine[], bp: number, serviceProductId: string | null): ServiceLine | null {
  if (!serviceProductId || bp <= 0) return null
  const tetri = serviceTetri(serviceBaseTetri(lines, serviceProductId), bp)
  if (tetri <= 0) return null
  const amount = tetri / 100
  return {
    product_id: serviceProductId, product_name: serviceName(bp), barcode: null, quantity: 1,
    unit_price: amount, total_price: amount, promotion_id: null, promo_discount: 0,
  }
}

/** The rate as the owner types it in the settings (a dot or a comma; empty = 0, off); null = the server would refuse
 *  it (TABLE_PCT_INVALID), so it is never sent. */
export function servicePctInput(raw: string): number | null {
  const s = raw.trim().replace(',', '.')
  if (s === '') return 0
  if (!PCT.test(s)) return null
  const n = Number(s)
  return n <= 30 ? n : null
}

/** What a table's checkout sends (spec G4–G6): the dishes as they are, then the service line last. The base is the
 *  lines' own total_price (a promotion inside it, the sale's discount not) — the trigger's rule. A rate without the
 *  service product is refused here: the trigger would refuse the bill, and the cashier is told now instead. */
export function checkoutLines<T extends ServiceBaseLine>(lines: T[], bp: number, serviceProductId: string | null): (T | ServiceLine)[] {
  if (bp <= 0) return lines
  if (!serviceProductId) throw new Error('TABLE_SERVICE_PRODUCT_MISSING')
  const svc = serviceLine(lines, bp, serviceProductId)
  return svc ? [...lines, svc] : lines
}

/** The service a table's checkout shows (₾, to the tetri) — the same figure checkoutLines sends as its line. Known
 *  without the product id, so the screen is right even while the till still has to read the service product. */
export function tableServiceAmount(lines: readonly ServiceBaseLine[], bp: number, serviceProductId: string | null): number {
  return serviceTetri(serviceBaseTetri(lines, serviceProductId), bp) / 100
}

/** Rankings (the reports' sold list): revenue counts the service, a product ranking leaves it out. */
export function withoutService<T extends { product_id: string | null }>(rows: readonly T[], serviceProductId: string | null): T[] {
  return serviceProductId ? rows.filter((r) => r.product_id !== serviceProductId) : [...rows]
}

/** The sale's `items_count` from the lines it sends — the service line counts (Codex final #1): a ₾0 sale paid by bonus
 *  decides a complete return by this count, so a dish + its service is two and returning the service alone is not all. */
export function itemsCountOf(lines: readonly { quantity: number }[]): number {
  return Math.round(lines.reduce((s, l) => s + l.quantity, 0) * 1000) / 1000
}
